import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Alert,
  Snackbar,
} from '@mui/material';
import { Add as AddIcon, Psychology as PsychologyIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { fetchKeywords, fetchKeywordStats } from '../../store/slices/aiInstructionSlice';
import KeywordList from './KeywordList';
import KeywordForm from './KeywordForm';
import KeywordStats from './KeywordStats';
import { useMobileLayout } from '../../hooks/useMobileLayout';

const AIInstructions: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { keywords, stats, loading, error } = useSelector((state: RootState) => state.aiInstructions);
  const { isSmallMobile, spacing } = useMobileLayout();

  const [formOpen, setFormOpen] = useState(false);
  const [selectedKeywordId, setSelectedKeywordId] = useState<number | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  useEffect(() => {
    dispatch(fetchKeywords({}));
    dispatch(fetchKeywordStats());
  }, [dispatch]);

  const handleOpenForm = (keywordId?: number) => {
    setSelectedKeywordId(keywordId || null);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setSelectedKeywordId(null);
  };

  const handleFormSuccess = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
    handleCloseForm();
    dispatch(fetchKeywords({}));
    dispatch(fetchKeywordStats());
  };

  const handleFormError = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarSeverity('error');
    setSnackbarOpen(true);
  };

  return (
    <Container maxWidth="xl" sx={{ py: spacing.sm }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: spacing.xs,
          mb: spacing.md,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PsychologyIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant={isSmallMobile ? 'h5' : 'h4'} fontWeight="bold">
            AI Instrukce
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenForm()}
          size={isSmallMobile ? 'medium' : 'large'}
          fullWidth={isSmallMobile}
        >
          Přidat klíčové slovo
        </Button>
      </Box>

      {/* Description */}
      <Paper
        sx={{
          p: spacing.sm,
          mb: spacing.md,
          backgroundColor: 'info.light',
          borderLeft: 4,
          borderColor: 'info.main',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          <strong>Jak to funguje:</strong> Definujte klíčová slova, která se vyhledávají v fakturách.
          Když AI najde klíčové slovo v dokumentu, použije vaše instrukce pro AI zpracování.
          Například: klíčové slovo "Novák s.r.o." → AI instrukce "Variabilní symbol je označený jako číslo faktury".
        </Typography>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: spacing.md }} onClose={() => {}}>
          {error}
        </Alert>
      )}

      {/* Statistics */}
      <KeywordStats stats={stats} loading={loading} />

      {/* Keywords List */}
      <Paper sx={{ mt: spacing.md }}>
        <KeywordList
          keywords={keywords}
          loading={loading}
          onEdit={handleOpenForm}
          onSuccess={handleFormSuccess}
          onError={handleFormError}
        />
      </Paper>

      {/* Keyword Form Dialog */}
      <KeywordForm
        open={formOpen}
        keywordId={selectedKeywordId}
        onClose={handleCloseForm}
        onSuccess={handleFormSuccess}
        onError={handleFormError}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AIInstructions;

